import { formatNorwegianDate } from '../../utils/dateUtils'
import i18n from '../../i18n'

export interface FilterSummaryInput {
  from: string
  to: string
  klasse: string[]
  omregistrering: boolean
  postnummerEnabled: boolean
  postnummerNavn: string | null
  /** Ståtid hides klasse and eierskifte — they don't reach its endpoints (#523). */
  showKlasseOgEierskifte: boolean
  /** True when any tab-local filter differs from its default. */
  tabFiltersActive: boolean
}

/**
 * The collapsed filter box's one-line summary, as segments.
 *
 * Every segment is always present (never "3 klasser" one moment and nothing
 * the next), because the line's whole job is to let someone read the state of
 * the query without opening anything. A filter that disappears when it is at
 * its default forces the reader to remember what is missing, which is the
 * problem the summary exists to remove.
 *
 * It describes the APPLIED filters, not the draft — it is what the numbers on
 * screen were produced with.
 */
export function filterSummary(i: FilterSummaryInput): string[] {
  const t = i18n.getFixedT(null, 'bruktbil', 'filterSummary')
  const out = [`${formatNorwegianDate(i.from)}–${formatNorwegianDate(i.to)}`]

  if (i.showKlasseOgEierskifte) {
    out.push(
      i.klasse.length === 0 ? t('alleKlasser')
        : i.klasse.length === 1 ? i.klasse[0]
        : t('nKlasser', { n: i.klasse.length }),
    )
    out.push(i.omregistrering ? t('kunOmregistrering') : t('alleEierskifter'))
  }

  // A named markedsområde is the useful label; enabled-but-unnamed still has
  // to say something, or an active geo filter reads as "Hele landet".
  out.push(
    !i.postnummerEnabled ? t('heleLandet')
      : i.postnummerNavn ?? t('minePostnummer'),
  )

  // Tab-local filters live in the same collapsed box, so their state has to
  // surface here too — otherwise collapsing the box hides a filter that is
  // still shaping the numbers.
  if (i.tabFiltersActive) out.push(t('tabfiltreAktive'))

  return out
}
